import { useState, useCallback } from "react";
import {
  GridRowId,
  GridRowSelectionModel,
  GridRowParams,
} from "@mui/x-data-grid-premium";

interface ContextMenuState {
  open: boolean;
  event: MouseEvent | null;
  selectedRows: any[];
}

interface UseRowSelectionOptions {
  apiRef: React.RefObject<any>;
  ContextMenuComponent?: React.ComponentType<any>;
}

/**
 * Predicate used to filter selected rows in `getSelectedRows`.
 *
 * @param id  - The row ID as stored in the selection model.
 * @param row - The full row object returned by the grid API.
 * @returns `true` to include the row in the result, `false` to exclude it.
 *
 * @example – children only (default behaviour)
 *   getSelectedRows()
 *
 * @example – all rows (children + group rows)
 *   getSelectedRows(() => true)
 *
 * @example – group rows only
 *   getSelectedRows((id) => String(id).startsWith("group-"))
 */
export type RowSelectionFilter = (id: GridRowId, row: any) => boolean;

interface UseRowSelectionReturn {
  rowSelectionModel: GridRowSelectionModel;
  setRowSelectionModel: React.Dispatch<
    React.SetStateAction<GridRowSelectionModel>
  >;
  contextMenuState: ContextMenuState;
  handleRowSelectionModelChange: (newSelection: GridRowSelectionModel) => void;
  handleRowClick: (params: GridRowParams, event: React.MouseEvent) => void;
  handleContextMenu: (event: React.MouseEvent) => void;
  closeContextMenu: () => void;
  getSelectedRows: (filter?: RowSelectionFilter) => any[];
}

/**
 * Hook for managing row selection state, context menu, and selection normalization.
 */
export function useRowSelection({
  apiRef,
  ContextMenuComponent,
}: UseRowSelectionOptions): UseRowSelectionReturn {
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: "include",
      ids: new Set<GridRowId>(),
    });

  const [contextMenuState, setContextMenuState] = useState<ContextMenuState>({
    open: false,
    event: null,
    selectedRows: [],
  });

  /**
   * Converts an "exclude" selection model (select all) to an "include" model
   * containing only the currently visible page rows.
   */
  const normalizeSelectionToCurrentPage = useCallback(
    (selection: GridRowSelectionModel): GridRowSelectionModel => {
      // If it's "exclude" mode with empty ids, user clicked "select all"
      // Convert to "include" mode with only current page rows
      if (selection.type === "exclude" && selection.ids.size === 0) {
        const visibleRowIds = new Set<GridRowId>();

        // getRowModels() returns only the currently loaded/visible rows
        const rowModels = apiRef.current?.getRowModels();
        if (rowModels) {
          rowModels.forEach((row: any, id: GridRowId) => {
            // Skip group rows (they have __groupKey__ property)
            if (row.__groupKey__ === undefined) {
              visibleRowIds.add(id);
            }
          });
        }

        return {
          type: "include",
          ids: visibleRowIds,
        };
      }

      return selection;
    },
    [apiRef]
  );

  /**
   * Filters out grouped parent row IDs (prefixed with 'group-') when
   * child IDs are also present, so they never coexist.
   */
  const filterGroupIds = useCallback((ids: Set<GridRowId>): GridRowId[] => {
    const allIds = Array.from(ids);
    const hasChildId = allIds.some((id) => !String(id).startsWith("group-"));
    if (hasChildId) {
      return allIds.filter((id) => !String(id).startsWith("group-"));
    }
    return allIds;
  }, []);

  const handleRowSelectionModelChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      const normalizedSelection = normalizeSelectionToCurrentPage(newSelection);
      setRowSelectionModel(normalizedSelection);
    },
    [normalizeSelectionToCurrentPage]
  );

  /**
   * When a row body is clicked (not the checkbox), select only that row
   * unless Ctrl/Cmd/Shift is held, in which case let the default multi-select behavior apply.
   */
  const handleRowClick = useCallback(
    (params: GridRowParams, event: React.MouseEvent) => {
      // If a modifier key is held, let the default DataGrid multi-select behavior handle it
      if (event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }

      // If the click originated from the checkbox cell, let DataGrid handle it natively
      const target = event.target as HTMLElement;
      if (target.closest(".MuiDataGrid-cellCheckbox")) {
        return;
      }

      // Prevent MUI DataGrid's default selection handling so it doesn't
      // add to the existing selection after we set our single-select model.
      (event as any).defaultMuiPrevented = true;

      // Single-select: only select the clicked row
      setRowSelectionModel({
        type: "include",
        ids: new Set<GridRowId>([params.id]),
      });
    },
    []
  );

  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      if (!ContextMenuComponent) return;

      // If the user has selected text, allow the native context menu so they can copy it
      const selection = window.getSelection();
      if (selection && selection.toString().trim().length > 0) {
        return;
      }

      event.preventDefault();

      // Get currently selected rows, excluding group parent IDs
      const selectedIds = filterGroupIds(rowSelectionModel.ids);
      let rowsForContextMenu: any[] = [];

      if (selectedIds.length > 0) {
        // Use existing selection
        rowsForContextMenu = selectedIds
          .map((id) => apiRef.current?.getRow(id))
          .filter(Boolean);
      } else {
        // Find the clicked row
        const target = event.target as HTMLElement;
        const rowElement = target.closest('[role="row"]');

        if (rowElement) {
          const rowId = rowElement.getAttribute("data-id");
          if (rowId) {
            const clickedRow = apiRef.current?.getRow(rowId);
            if (clickedRow) {
              rowsForContextMenu = [clickedRow];
            }
          }
        }
      }

      if (rowsForContextMenu.length > 0) {
        setContextMenuState({
          open: true,
          event: event.nativeEvent,
          selectedRows: rowsForContextMenu,
        });
      }
    },
    [ContextMenuComponent, rowSelectionModel.ids, apiRef, filterGroupIds]
  );

  const closeContextMenu = useCallback(() => {
    setContextMenuState({
      open: false,
      event: null,
      selectedRows: [],
    });
  }, []);

  /**
   * Returns complete row objects for all selected rows.
   *
   * By default, group rows (IDs prefixed with `"group-"`) are excluded so
   * only child/leaf rows are returned.
   *
   * Pass a `filter` predicate to override this behaviour:
   *   - `() => true`                              – include every selected row (children + groups)
   *   - `(id) => String(id).startsWith("group-")` – group rows only
   *   - `(id) => !String(id).startsWith("group-")`– children only (same as default)
   */
  const getSelectedRows = useCallback(
    (filter?: RowSelectionFilter) => {
      const allIds = Array.from(rowSelectionModel.ids);
      const selectedIds = filter
        ? allIds.filter((id) => filter(id, apiRef.current?.getRow(id)))
        : allIds.filter((id) => !String(id).startsWith("group-"));

      return selectedIds
        .map((id) => ({ id, ...apiRef.current?.getRow(id) }))
        .filter(Boolean);
    },
    [rowSelectionModel.ids, apiRef]
  );

  return {
    rowSelectionModel,
    setRowSelectionModel,
    contextMenuState,
    handleRowSelectionModelChange,
    handleRowClick,
    handleContextMenu,
    closeContextMenu,
    getSelectedRows,
  };
}
